import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Calendar, Loader2, FileSpreadsheet, FileText, Copy, Check, ChevronLeft, ChevronRight, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ZohoCustomerLink, ZohoInvoiceLink } from '../../components/ZohoLinks';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function formatDate(d) { return d.toISOString().split('T')[0]; }

const COLS = [
  { key: 'trainer_name', label: 'Trainer' },
  { key: 'batch_number', label: 'Batch No.' },
  { key: 'batch_name', label: 'Batch Name' },
  { key: 'batch_conducted', label: 'Conducted' },
  { key: 'timing', label: 'Timing' },
  { key: 'batch_strength', label: 'Strength' },
  { key: 'present', label: 'Present' },
  { key: 'absent', label: 'Absent' },
  { key: 'mode_of_training', label: 'Mode' },
  { key: 'not_taken_reason', label: 'Reason' },
  { key: 'trainer_comments', label: 'Comments' },
];

export default function DprReportPage({ isAdmin = false }) {
  const [date, setDate] = useState(formatDate(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [conductedFilter, setConductedFilter] = useState('all');
  const [selectedTrainers, setSelectedTrainers] = useState([]);
  const [trainerSearch, setTrainerSearch] = useState('');
  const [trainerDropOpen, setTrainerDropOpen] = useState(false);
  const dropRef = useRef(null);
  const [batchModal, setBatchModal] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchDetail, setBatchDetail] = useState(null);
  const [absentModal, setAbsentModal] = useState(false);
  const [absentStudents, setAbsentStudents] = useState([]);
  const [absentLoading, setAbsentLoading] = useState(false);

  const fetchDpr = useCallback(() => {
    setLoading(true);
    const url = isAdmin ? `/training/daily-attendance/dpr-all/${date}` : `/trainer/daily-attendance/dpr-all/${date}`;
    api.get(url).then(r => setData(r.data)).catch(() => toast.error('Failed to load DPR')).finally(() => setLoading(false));
  }, [date, isAdmin]);

  useEffect(() => { fetchDpr(); }, [fetchDpr]);
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setTrainerDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const prevDay = () => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(formatDate(d)); };
  const nextDay = () => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(formatDate(d)); };
  const goToday = () => setDate(formatDate(new Date()));

  const allRows = data?.rows || [];
  const trainers = data?.trainers || [];
  const filteredTrainerList = useMemo(() => trainers.filter(t => t.name.toLowerCase().includes(trainerSearch.toLowerCase())), [trainers, trainerSearch]);

  const rows = useMemo(() => allRows.filter(r => {
    if (conductedFilter === 'yes' && r.batch_conducted !== 'Yes') return false;
    if (conductedFilter === 'no' && r.batch_conducted !== 'No') return false;
    if (selectedTrainers.length > 0 && !selectedTrainers.includes(r.trainer_id || r.trainer_name)) return false;
    return true;
  }), [allRows, conductedFilter, selectedTrainers]);

  const totalConducted = rows.filter(r => r.batch_conducted === 'Yes').length;
  const totalNotConducted = rows.filter(r => r.batch_conducted === 'No').length;
  const totalPresent = rows.reduce((s, r) => s + r.present, 0);
  const totalAbsent = rows.reduce((s, r) => s + r.absent, 0);
  const totalStrength = rows.reduce((s, r) => s + r.batch_strength, 0);

  // Trainer summary
  const trainerSummary = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (!m[r.trainer_name]) m[r.trainer_name] = { batches: 0, conducted: 0, not_conducted: 0, present: 0, absent: 0, strength: 0 };
      const t = m[r.trainer_name];
      t.batches++;
      if (r.batch_conducted === 'Yes') t.conducted++; else t.not_conducted++;
      t.present += r.present; t.absent += r.absent; t.strength += r.batch_strength;
    });
    return m;
  }, [rows]);

  const toggleTrainer = (id) => setSelectedTrainers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const copyToClipboard = () => {
    const header = `DPR - ${data?.day_name || ''}, ${date}\n\n`;
    const grouped = {};
    rows.forEach(r => { grouped[r.trainer_name] = grouped[r.trainer_name] || []; grouped[r.trainer_name].push(r); });
    let text = header;
    Object.entries(grouped).forEach(([trainer, trows]) => {
      text += `--- ${trainer} ---\n`;
      trows.forEach((r, i) => {
        text += `${i + 1}. ${r.batch_name} (#${r.batch_number})\n   Conducted: ${r.batch_conducted} | Mode: ${r.mode_of_training}\n   Strength: ${r.batch_strength} (P:${r.present}, A:${r.absent})${r.not_taken_reason ? `\n   Reason: ${r.not_taken_reason}` : ''}${r.trainer_comments ? `\n   Comments: ${r.trainer_comments}` : ''}\n`;
      });
      text += '\n';
    });
    navigator.clipboard.writeText(text);
    setCopied(true); toast.success('Copied'); setTimeout(() => setCopied(false), 2000);
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map(r => { const o = {}; COLS.forEach(c => { o[c.label] = r[c.key]; }); return o; }));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'DPR');
    saveAs(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]), `DPR_${date}.xlsx`);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16); doc.text('Daily Progress Report', 14, 18);
    doc.setFontSize(10); doc.text(`${data?.day_name || ''}, ${date}`, 14, 26);
    autoTable(doc, { startY: 32, head: [COLS.map(c => c.label)], body: rows.map(r => COLS.map(c => r[c.key] ?? '-')), styles: { fontSize: 8 }, headStyles: { fillColor: [109, 40, 217] } });
    doc.save(`DPR_${date}.pdf`);
  };

  const openBatchModal = async (row) => {
    if (!row.batch_id) { toast.error('Batch ID not available'); return; }
    setBatchModal(row); setBatchLoading(true); setBatchDetail(null);
    try { const r = await api.get(`/training/batches/${row.batch_id}`); setBatchDetail(r.data); } catch { toast.error('Failed to load batch'); }
    setBatchLoading(false);
  };

  const openAbsentModal = async () => {
    setAbsentModal(true); setAbsentLoading(true); setAbsentStudents([]);
    try {
      const r = await api.get(`/training/daily-attendance/absent-students/${date}`);
      setAbsentStudents(r.data.students || []);
    } catch { toast.error('Failed to load absent students'); }
    setAbsentLoading(false);
  };

  const summaryRef = useRef(null);

  const exportSummaryPdf = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16); doc.text('Trainer Summary', 14, 18);
    doc.setFontSize(10); doc.text(`${data?.day_name || ''}, ${date}`, 14, 26);
    const entries = Object.entries(trainerSummary).sort((a, b) => a[0].localeCompare(b[0]));
    autoTable(doc, {
      startY: 32,
      head: [['Trainer', 'Batches', 'Conducted', 'Not Conducted', 'Strength', 'Present', 'Absent']],
      body: entries.map(([name, s]) => [name, s.batches, s.conducted, s.not_conducted, s.strength, s.present, s.absent]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [109, 40, 217] },
    });
    doc.save(`Trainer_Summary_${date}.pdf`);
  };

  return (
    <div className="space-y-6" data-testid="dpr-report-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">DPR Report</h1>
          <p className="text-sm text-slate-400 mt-0.5">Daily Progress Report — {isAdmin ? 'All trainers' : 'Your batches'}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={copyToClipboard} data-testid="dpr-page-copy-btn">
            {copied ? <Check className="w-3 h-3 mr-1 text-emerald-500" /> : <Copy className="w-3 h-3 mr-1" />}{copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportExcel}><FileSpreadsheet className="w-3 h-3 mr-1" />Excel</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={exportPdf}><FileText className="w-3 h-3 mr-1" />PDF</Button>
        </div>
      </div>

      {/* Date picker + Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="w-8 h-8" onClick={prevDay}><ChevronLeft className="w-4 h-4" /></Button>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8 px-3 text-xs border rounded-md bg-white" data-testid="dpr-date-picker" />
          <Button variant="outline" size="icon" className="w-8 h-8" onClick={nextDay}><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" className="h-8 text-xs ml-1" onClick={goToday}>Today</Button>
        </div>
        {data?.day_name && (
          <Badge variant="outline" className={`text-[10px] font-semibold uppercase ${data.is_weekend ? 'bg-violet-50 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
            {data.day_name} &middot; {data.is_weekend ? 'Weekend' : 'Weekday'}
          </Badge>
        )}
        {/* Conducted filter */}
        <div className="flex items-center gap-1 border rounded-md p-0.5 bg-white" data-testid="conducted-filter">
          {[{ v: 'all', l: 'All' }, { v: 'yes', l: 'Conducted' }, { v: 'no', l: 'Not Taken' }].map(f => (
            <button key={f.v} onClick={() => setConductedFilter(f.v)} className={`px-2.5 py-1 text-[10px] font-medium rounded-sm transition-colors ${conductedFilter === f.v ? 'bg-violet-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`} data-testid={`conducted-${f.v}`}>
              {f.l}
            </button>
          ))}
        </div>
        {/* Multi-select trainer */}
        {isAdmin && trainers.length > 0 && (
          <div className="relative" ref={dropRef}>
            <button onClick={() => setTrainerDropOpen(!trainerDropOpen)} className="h-8 px-3 text-xs border rounded-md bg-white flex items-center gap-1.5 min-w-[180px] hover:border-violet-300" data-testid="dpr-trainer-filter">
              <Search className="w-3 h-3 text-slate-400" />
              {selectedTrainers.length > 0 ? <span className="text-violet-700 font-medium">{selectedTrainers.length} trainer{selectedTrainers.length > 1 ? 's' : ''}</span> : <span className="text-slate-400">All Trainers</span>}
            </button>
            {trainerDropOpen && (
              <div className="absolute top-9 left-0 z-50 bg-white border rounded-md shadow-lg w-64 max-h-72 overflow-hidden">
                <div className="p-2 border-b">
                  <Input placeholder="Search trainers..." value={trainerSearch} onChange={e => setTrainerSearch(e.target.value)} className="h-7 text-xs" autoFocus data-testid="trainer-search-input" />
                </div>
                {selectedTrainers.length > 0 && <button onClick={() => setSelectedTrainers([])} className="w-full px-3 py-1.5 text-[10px] text-violet-600 hover:bg-violet-50 text-left font-medium">Clear all</button>}
                <div className="overflow-y-auto max-h-48">
                  {filteredTrainerList.map(t => (
                    <label key={t.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={selectedTrainers.includes(t.id)} onChange={() => toggleTrainer(t.id)} className="w-3.5 h-3.5 rounded border-slate-300 text-violet-600" />
                      <span className="text-xs text-slate-700 truncate">{t.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {selectedTrainers.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {selectedTrainers.map(id => {
              const t = trainers.find(x => x.id === id);
              return <Badge key={id} variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200 gap-1 pr-1">{t?.name || id}<button onClick={() => toggleTrainer(id)}><X className="w-2.5 h-2.5" /></button></Badge>;
            })}
          </div>
        )}
      </div>

      {/* KPI Cards — clickable */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="border p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total Batches</p>
          <p className="text-xl font-bold text-slate-800">{rows.length}</p>
        </Card>
        <Card className="border p-3 cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => setConductedFilter(conductedFilter === 'yes' ? 'all' : 'yes')} data-testid="kpi-conducted">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Conducted</p>
          <p className="text-xl font-bold text-emerald-600">{totalConducted}<span className="text-sm text-slate-400 font-normal ml-1">/ {rows.length}</span></p>
        </Card>
        <Card className="border p-3 cursor-pointer hover:border-rose-300 transition-colors" onClick={() => setConductedFilter(conductedFilter === 'no' ? 'all' : 'no')} data-testid="kpi-not-conducted">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Not Conducted</p>
          <p className="text-xl font-bold text-rose-500">{totalNotConducted}</p>
        </Card>
        <Card className="border p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Total Strength</p>
          <p className="text-xl font-bold text-slate-800">{totalStrength}</p>
        </Card>
        <Card className="border p-3">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Present</p>
          <p className="text-xl font-bold text-emerald-600">{totalPresent}</p>
        </Card>
        <Card className="border p-3 cursor-pointer hover:border-rose-300 hover:shadow-sm transition-all" onClick={openAbsentModal} data-testid="kpi-absent">
          <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Absent</p>
          <p className="text-xl font-bold text-rose-600">{totalAbsent}</p>
          <p className="text-[9px] text-slate-400 mt-0.5">Click for details</p>
        </Card>
      </div>

      {/* DPR Table */}
      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
      ) : rows.length === 0 ? (
        <Card className="border p-16 text-center">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No attendance data for {date}</p>
        </Card>
      ) : (
        <Card className="border p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>{COLS.map(c => <TableHead key={c.key} className="text-[9px] uppercase">{c.label}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} className={`cursor-pointer hover:bg-violet-50/40 transition-colors ${r.batch_conducted === 'No' ? 'bg-slate-50/50' : ''}`} onClick={() => openBatchModal(r)} data-testid={`dpr-row-${i}`}>
                  <TableCell className="text-xs font-medium text-slate-700">{r.trainer_name}</TableCell>
                  <TableCell className="text-xs font-bold text-violet-600">#{r.batch_number}</TableCell>
                  <TableCell className="text-xs font-medium">{r.batch_name}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[9px] ${r.batch_conducted === 'Yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{r.batch_conducted}</Badge></TableCell>
                  <TableCell className="text-[10px] font-mono text-slate-500">{r.timing || '-'}</TableCell>
                  <TableCell className="text-xs">{r.batch_strength}</TableCell>
                  <TableCell className="text-xs font-semibold text-emerald-600">{r.present}</TableCell>
                  <TableCell className={`text-xs font-semibold ${r.absent > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{r.absent}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[9px]">{r.mode_of_training || '-'}</Badge></TableCell>
                  <TableCell className="text-[10px] text-slate-500 max-w-[120px] truncate" title={r.not_taken_reason || ''}>{r.not_taken_reason || '-'}</TableCell>
                  <TableCell className="text-[10px] text-slate-500 max-w-[120px] truncate" title={r.trainer_comments || ''}>{r.trainer_comments || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Trainer Summary — below DPR table */}
      {isAdmin && Object.keys(trainerSummary).length > 0 && (
        <Card className="border p-0 overflow-hidden" ref={summaryRef}>
          <div className="px-4 py-2.5 border-b bg-slate-50/50 flex items-center justify-between">
            <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider">Trainer Summary{selectedTrainers.length > 0 ? ` (${selectedTrainers.length} selected)` : ''}</p>
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={exportSummaryPdf} data-testid="summary-pdf-btn">
              <FileText className="w-3 h-3" />Download PDF
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[9px] uppercase">Trainer</TableHead>
                <TableHead className="text-[9px] uppercase">Batches</TableHead>
                <TableHead className="text-[9px] uppercase">Conducted</TableHead>
                <TableHead className="text-[9px] uppercase">Not Conducted</TableHead>
                <TableHead className="text-[9px] uppercase">Strength</TableHead>
                <TableHead className="text-[9px] uppercase">Present</TableHead>
                <TableHead className="text-[9px] uppercase">Absent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(trainerSummary).sort((a, b) => a[0].localeCompare(b[0])).map(([name, s]) => (
                <TableRow key={name}>
                  <TableCell className="text-xs font-medium text-slate-800">{name}</TableCell>
                  <TableCell className="text-xs">{s.batches}</TableCell>
                  <TableCell className="text-xs font-semibold text-emerald-600">{s.conducted}</TableCell>
                  <TableCell className={`text-xs font-semibold ${s.not_conducted > 0 ? 'text-rose-500' : 'text-slate-300'}`}>{s.not_conducted}</TableCell>
                  <TableCell className="text-xs">{s.strength}</TableCell>
                  <TableCell className="text-xs font-semibold text-emerald-600">{s.present}</TableCell>
                  <TableCell className={`text-xs font-semibold ${s.absent > 0 ? 'text-rose-600' : 'text-slate-300'}`}>{s.absent}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Batch Detail Modal */}
      <BatchDetailModal batchModal={batchModal} batchLoading={batchLoading} batchDetail={batchDetail} onClose={() => { setBatchModal(null); setBatchDetail(null); }} />

      {/* Absent Students Modal */}
      <AbsentStudentsModal open={absentModal} loading={absentLoading} students={absentStudents} date={date} onClose={() => setAbsentModal(false)} />
    </div>
  );
}

/* ─── Batch Detail Modal ─── */
function BatchDetailModal({ batchModal, batchLoading, batchDetail, onClose }) {
  const [expandedStudent, setExpandedStudent] = useState(null);
  if (!batchModal) return null;
  return (
    <Dialog open={!!batchModal} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Batch #{batchModal?.batch_number} — {batchModal?.batch_name}</DialogTitle></DialogHeader>
        {batchLoading ? (
          <div className="h-32 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : batchDetail ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div><span className="text-slate-400">Trainer</span><p className="font-medium text-slate-800">{batchDetail.trainer_name || '-'}</p></div>
              <div><span className="text-slate-400">Status</span><p><Badge variant="outline" className="text-[9px]">{batchDetail.batch_status || '-'}</Badge></p></div>
              <div><span className="text-slate-400">Course</span><p className="font-medium text-slate-800">{batchDetail.course_name || batchDetail.course || '-'}</p></div>
              <div><span className="text-slate-400">Centre</span><p className="font-medium text-slate-800">{batchDetail.centre_name || batchDetail.centre || '-'}</p></div>
              <div><span className="text-slate-400">Timing</span><p className="font-medium text-slate-800">{batchDetail.start_time || ''} - {batchDetail.end_time || ''}</p></div>
              <div><span className="text-slate-400">Students</span><p className="font-medium text-slate-800">{batchDetail.student_count || 0}</p></div>
            </div>
            <div className="bg-slate-50 rounded-md p-3 space-y-1">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Today's Attendance</p>
              <div className="flex gap-4 text-xs">
                <span>Conducted: <strong className={batchModal.batch_conducted === 'Yes' ? 'text-emerald-600' : 'text-slate-500'}>{batchModal.batch_conducted}</strong></span>
                <span>Mode: <strong>{batchModal.mode_of_training || '-'}</strong></span>
                <span>Present: <strong className="text-emerald-600">{batchModal.present}</strong></span>
                <span>Absent: <strong className="text-rose-600">{batchModal.absent}</strong></span>
              </div>
              {batchModal.not_taken_reason && <p className="text-xs text-slate-600 mt-1"><span className="text-slate-400">Reason:</span> {batchModal.not_taken_reason}</p>}
              {batchModal.trainer_comments && <p className="text-xs text-slate-600"><span className="text-slate-400">Comments:</span> {batchModal.trainer_comments}</p>}
            </div>
            {batchDetail.students?.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Students ({batchDetail.students.length})</p>
                <StudentTable students={batchDetail.students} expandedStudent={expandedStudent} setExpandedStudent={setExpandedStudent} />
              </div>
            )}
          </div>
        ) : <p className="text-sm text-slate-400 text-center py-8">Failed to load batch details</p>}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Absent Students Modal ─── */
function AbsentStudentsModal({ open, loading, students, date, onClose }) {
  const [expandedStudent, setExpandedStudent] = useState(null);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Absent Students — {date}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="h-32 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : students.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No absent students found for this date</p>
        ) : (
          <div>
            <p className="text-xs text-slate-400 mb-2">{students.length} student{students.length !== 1 ? 's' : ''} absent</p>
            <StudentTable students={students} expandedStudent={expandedStudent} setExpandedStudent={setExpandedStudent} showBatch />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Shared Student Table with drill-down ─── */
const PAY_BADGE = { paid: 'bg-emerald-50 text-emerald-600 border-emerald-200', overdue: 'bg-red-50 text-red-600 border-red-200', partially_paid: 'bg-amber-50 text-amber-600 border-amber-200', sent: 'bg-blue-50 text-blue-600 border-blue-200' };

function StudentTable({ students, expandedStudent, setExpandedStudent, showBatch = false }) {
  return (
    <div className="border rounded-md overflow-hidden max-h-[380px] overflow-y-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-white z-10">
          <TableRow>
            <TableHead className="text-[9px] uppercase">Enr. No.</TableHead>
            <TableHead className="text-[9px] uppercase">Name</TableHead>
            {showBatch && <TableHead className="text-[9px] uppercase">Batch</TableHead>}
            <TableHead className="text-[9px] uppercase">Course</TableHead>
            <TableHead className="text-[9px] uppercase">Payment</TableHead>
            <TableHead className="text-[9px] uppercase">Type</TableHead>
            <TableHead className="text-[9px] uppercase w-6"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {students.map(s => {
            const sid = s.id || s.student_id || s.enrollment_no;
            const isExpanded = expandedStudent === sid;
            return (
              <React.Fragment key={sid}>
                <TableRow className="cursor-pointer hover:bg-violet-50/30" onClick={() => setExpandedStudent(isExpanded ? null : sid)}>
                  <TableCell className="text-xs">
                    {s.enrollment_no && s.zoho_customer_id ? (
                      <span onClick={e => e.stopPropagation()}>
                        <ZohoCustomerLink enrollmentNo={`#${s.enrollment_no}`} zohoCustomerId={s.zoho_customer_id} className="text-xs font-bold" />
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-violet-600">{s.enrollment_no ? `#${s.enrollment_no}` : '-'}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-medium text-slate-800">{s.name}</TableCell>
                  {showBatch && <TableCell className="text-[10px] text-slate-500">#{s.batch_number} {s.batch_name}</TableCell>}
                  <TableCell className="text-[10px] text-slate-500">{s.course || '-'}</TableCell>
                  <TableCell>
                    {s.payment_status ? (
                      <Badge variant="outline" className={`text-[8px] ${PAY_BADGE[s.payment_status] || 'text-slate-500'}`}>{s.payment_status.replace('_', ' ')}</Badge>
                    ) : s.invoices?.length > 0 ? (
                      <Badge variant="outline" className={`text-[8px] ${PAY_BADGE[s.invoices[0].status] || 'text-slate-500'}`}>{(s.invoices[0].status || '').replace('_', ' ')}</Badge>
                    ) : <span className="text-[10px] text-slate-300">-</span>}
                  </TableCell>
                  <TableCell><Badge variant="outline" className="text-[8px]">{s.enrollment_type || 'regular'}</Badge></TableCell>
                  <TableCell className="p-1">{isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}</TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={showBatch ? 7 : 6} className="bg-slate-50/60 p-0">
                      <div className="px-4 py-3 space-y-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                          <div><span className="text-slate-400 text-[10px]">Phone</span><p className="text-slate-700">{s.phone || '-'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Email</span><p className="text-slate-700">{s.email || '-'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Branch</span><p className="text-slate-700">{s.branch || '-'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Total</span><p className="text-slate-700">{s.total_amount ? `₹${Number(s.total_amount).toLocaleString('en-IN')}` : '-'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Paid</span><p className="text-slate-700">{s.paid_amount ? `₹${Number(s.paid_amount).toLocaleString('en-IN')}` : '-'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Due</span><p className={s.due_amount > 0 ? 'text-rose-600 font-medium' : 'text-slate-700'}>{s.due_amount ? `₹${Number(s.due_amount).toLocaleString('en-IN')}` : '₹0'}</p></div>
                          <div><span className="text-slate-400 text-[10px]">Salesperson</span><p className="text-slate-700">{s.salesperson || '-'}</p></div>
                          {s.counsellor_name && <div><span className="text-slate-400 text-[10px]">Counsellor</span><p className="text-slate-700">{s.counsellor_name}</p></div>}
                          {showBatch && s.trainer_name && <div><span className="text-slate-400 text-[10px]">Trainer</span><p className="text-slate-700">{s.trainer_name}</p></div>}
                        </div>
                        {s.invoices?.length > 0 && (
                          <div>
                            <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Invoices</p>
                            <div className="flex flex-wrap gap-2">
                              {s.invoices.map(inv => (
                                <div key={inv.invoice_id} className="inline-flex items-center gap-1.5 text-xs bg-white border border-slate-200 rounded px-2.5 py-1" onClick={e => e.stopPropagation()}>
                                  <ZohoInvoiceLink invoiceNumber={inv.invoice_number} invoiceId={inv.invoice_id} className="text-[11px]" />
                                  <Badge variant="outline" className={`text-[8px] px-1 py-0 ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : inv.status === 'overdue' ? 'bg-red-50 text-red-600 border-red-200' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-600 border-amber-200' : 'text-slate-500'}`}>
                                    {inv.status}
                                  </Badge>
                                  <span className="text-[10px] text-slate-500">₹{Number(inv.total || 0).toLocaleString('en-IN')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
